// Rabbit runner.
//
// The "Rabbit" is captured by the first player to win a hole outright (unique
// low score, gross or net per the option). A tied hole leaves ownership
// unchanged. Two segments, each with its own pot:
//   • Whoever holds the Rabbit after hole 9 wins the FRONT-9 pot.
//   • The Rabbit then resets (loose) for the back nine.
//   • Whoever holds it after hole 18 wins the BACK-9 pot.
//
// Betting: a single Bet per Player ante. The round total is split evenly into
// the two pots — e.g. $10/player × 4 players = $40 → a $20 front pot and a $20
// back pot. The pot's whole value goes to that segment's Rabbit-holder; if the
// Rabbit is loose at a segment's end (no outright winner yet), that pot pays
// nothing. A segment only settles once its final hole (9 / 18) is fully scored.
//
// Pure function — no React, no state.

import { Course, Hole, Player, ScoreMap } from "../types";
import { HandicapMode, adjustHandicaps } from "../handicapAdjust";

// WHS-style course handicap — duplicated from the other runners so this file
// stays standalone.
function courseHandicap(
  handicapIndex: number | undefined,
  slope: number | undefined,
  rating: number | undefined,
  totalPar: number,
): number {
  if (handicapIndex == null) return 0;
  const slopeFactor = slope ? slope / 113 : 1;
  const ratingDiff = rating != null ? rating - totalPar : 0;
  return Math.round(handicapIndex * slopeFactor + ratingDiff);
}

function strokesOnHole(courseHcp: number, hole: Hole): number {
  if (courseHcp <= 0) return 0;
  const base = Math.floor(courseHcp / 18);
  const remainder = courseHcp % 18;
  return base + (hole.si <= remainder ? 1 : 0);
}

export type RabbitScoringMode = "net" | "gross";

export interface RabbitOptions {
  betPerPlayer?: number;
  scoringMode?: RabbitScoringMode;
  // Handicap policy ("full" | "playOffLow") — only meaningful in net. Mirrors
  // Stableford so the scorecard's stroke indicators respond the same way.
  handicapMode?: HandicapMode;
}

export interface RabbitResult {
  payouts: Record<string, { total: number }>;
  // The Rabbit-holder at the end of each segment (live during play — settles
  // once the segment's last hole is fully scored). null when the Rabbit is
  // still loose.
  frontWinner: string | null;
  backWinner: string | null;
  // True once the segment's final hole (9 / 18) has every player's score in —
  // i.e. the pot has been awarded.
  frontSettled: boolean;
  backSettled: boolean;
  // Cash value of each pot (same for both): betPerPlayer × players ÷ 2.
  frontPot: number;
  backPot: number;
  hasAnyBet: boolean;
}

export interface RabbitInput {
  course: Course;
  players: Player[];
  scores: ScoreMap;
  options: RabbitOptions;
}

export function runRabbit(input: RabbitInput): RabbitResult {
  const { course, players, scores, options } = input;
  const bet = Number(options.betPerPlayer) || 0;
  const scoringMode: RabbitScoringMode =
    options.scoringMode === "net" ? "net" : "gross";

  const payouts: Record<string, { total: number }> = {};
  for (const p of players) payouts[p.id] = { total: 0 };

  const empty: RabbitResult = {
    payouts,
    frontWinner: null,
    backWinner: null,
    frontSettled: false,
    backSettled: false,
    frontPot: 0,
    backPot: 0,
    hasAnyBet: false,
  };
  if (bet <= 0 || players.length < 2) return empty;

  // Net scoring setup. Handicap policy only applies to net (gross ignores
  // handicaps entirely).
  const adjusted =
    scoringMode === "net"
      ? adjustHandicaps(players, options.handicapMode)
      : players;
  const totalPar = course.holes.reduce((s, h) => s + h.par, 0);
  const tees = course.tees || [];
  const courseHcpById: Record<string, number> = {};
  for (const p of adjusted) {
    const tee = tees.find((t) => t.id === p.tee);
    courseHcpById[p.id] = courseHandicap(
      p.handicap,
      tee?.slope,
      tee?.rating,
      totalPar,
    );
  }

  const scoreFor = (playerId: string, hole: Hole): number | null => {
    const sc = (scores[playerId] || {})[hole.number];
    if (!sc || typeof sc.gross !== "number") return null;
    if (scoringMode === "gross") return sc.gross;
    return sc.gross - strokesOnHole(courseHcpById[playerId], hole);
  };

  // Walk a segment in hole order, transferring the Rabbit to any outright
  // (unique-low) hole winner. Incomplete holes (a missing score) and ties
  // leave ownership unchanged. Returns the holder after the segment and
  // whether the segment's final hole is fully scored.
  const walk = (lo: number, hi: number) => {
    const holes = course.holes
      .filter((h) => h.number >= lo && h.number <= hi)
      .sort((a, b) => a.number - b.number);
    let holder: string | null = null;
    for (const hole of holes) {
      const scored = players.map((p) => ({
        id: p.id,
        s: scoreFor(p.id, hole),
      }));
      if (scored.some((x) => x.s == null)) continue; // hole not complete
      const min = Math.min(...scored.map((x) => x.s as number));
      const lowOnes = scored.filter((x) => x.s === min);
      if (lowOnes.length === 1) holder = lowOnes[0].id; // outright → capture
    }
    const lastHole = course.holes.find((h) => h.number === hi);
    const settled =
      !!lastHole && players.every((p) => scoreFor(p.id, lastHole) != null);
    return { holder, settled };
  };

  const front = walk(1, 9);
  const back = walk(10, 18);

  const antePerPot = bet / 2;
  const potPerSegment = antePerPot * players.length;

  const settle = (winnerId: string | null) => {
    if (!winnerId) return; // Rabbit loose at segment end — pot pays nothing
    for (const p of players) {
      if (p.id === winnerId) payouts[p.id].total += potPerSegment - antePerPot;
      else payouts[p.id].total -= antePerPot;
    }
  };
  if (front.settled) settle(front.holder);
  if (back.settled) settle(back.holder);

  return {
    payouts,
    frontWinner: front.holder,
    backWinner: back.holder,
    frontSettled: front.settled,
    backSettled: back.settled,
    frontPot: potPerSegment,
    backPot: potPerSegment,
    hasAnyBet: true,
  };
}
